// Small building blocks shared by the sharing screens: a list row, a section with a title and an
// action, and the "could not load" state with a retry.
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = __css({"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fod5ikn","Bg96gwp":"faaz57k","Bhrd7zp":"fl43uef"},"description":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"row":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fnllxr2","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","E5pizo":"f1whvlc6","De3pzq":"fxugw4r","gggo0z":"fi15kbk","Behc1zw":"f10zy6vx","B4m11q7":"f32hy0g","tzxy30":"fgmlvap","B834pou":"f1taw3pw","uk9mye":"f1p2dij1","rm1204":"fwyddsq","Bwezv66":"fwl1lcr","F1poyb":"foi3q75","mbw9mh":"f1a566p","ijz38e":"f1ftuoky","Ctt3hk":"f1crbv9w"},"body":{"Bh6795r":"fqerorx","xawz":"fuk1rnt","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"title":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9","Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"details":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"f1xcgk68","Belr9w4":"f1j0q4x9","jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Byoj8tv":0,"uwmqm3":0,"z189sj":0,"z8tnut":0,"B0ocmuz":"f1mk8lai","dclx09":"ftrb29c","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy"}});

export interface SharingSectionProps {
  /** Id of the heading (the section is labelled by it). */
  id: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}

/** A titled block of a sharing tab, with its actions on the title line. */
export function SharingSection({ id, title, description, actions, children }: SharingSectionProps) {
  const styles = useStyles();
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.head}>
        <h2 id={id} className={styles.heading}>
          {title}
        </h2>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {description ? <p className={styles.description}>{description}</p> : null}
      {children}
    </section>
  );
}

export function RowList({ children, label }: { children: ReactNode; label: string }) {
  const styles = useStyles();
  return (
    <ul className={styles.list} aria-label={label}>
      {children}
    </ul>
  );
}

export interface SharingRowProps {
  /** Name and status chips. */
  title: ReactNode;
  /** Short facts under the title (scope, rights, dates…), each a separate entry. */
  details: readonly string[];
  actions?: ReactNode;
}

export function SharingRow({ title, details, actions }: SharingRowProps) {
  const styles = useStyles();
  return (
    <li className={styles.row}>
      <div className={styles.body}>
        <div className={styles.title}>{title}</div>
        <ul className={styles.details}>
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </li>
  );
}

/** The "could not load" message of a list, with a retry. */
export function LoadState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <MessageBar intent="error" layout="multiline">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
      <MessageBarActions>
        <Button onClick={onRetry}>{t('actions.retry')}</Button>
      </MessageBarActions>
    </MessageBar>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/sharing-ui.griffel.css";